import React, { useState, useEffect } from 'react';
import {
  Box,
  TextField,
  Button,
  Grid,
  InputAdornment,
  IconButton,
  Typography,
  useMediaQuery,
  useTheme,
  Collapse,
} from '@mui/material';
import Visibility from '@mui/icons-material/Visibility';
import VisibilityOff from '@mui/icons-material/VisibilityOff';
import { postApiCall } from 'api/apiService';
import { RESET_PASSWORD } from 'constant/app.config';
import { poppins300 } from 'theme/common.style';
import Swal from 'sweetalert2';
import Loading from 'assets/images/loading.png';
import PasswordValidation from 'component/SignupForm/PasswordValidation';

const ShowNewPassword = ({
  otpResponseObj,
  setShowsendOTP,
  setShowNewPassword,
  setShowForgotPassword,
}) => {
  const theme = useTheme();
  const matchesmd = useMediaQuery(theme.breakpoints.down('md'));
  const [formValues, setFormValues] = useState({
    newPassword: {
      value: '',
      showPassword: false,
      error: false,
      errorMessage: '',
    },
    confirmPassword: {
      value: '',
      showPassword: false,
      error: false,
      errorMessage: '',
    },
  });

  const validatePassword = (password) => {
    setPasswordValidation({
      minimumLength: password.length >= 8,
      number: /\d/.test(password),
      specialCharacter: /[!@#$%^&*(),.?":{}|<>]/.test(password),
      capitalCase: /[A-Z]/.test(password),
      lowerCase: /[a-z]/.test(password),
    });
  };

  const handleInputChange = (field, value) => {
    if (field === 'newPassword') {
      validatePassword(value);
    }

    setFormValues((prev) => {
      const updatedValues = {
        ...prev,
        [field]: {
          ...prev[field],
          value,
          error: false,
          errorMessage: '',
        },
      };

      const newPassword =
        field === 'newPassword' ? value : updatedValues.newPassword.value;

      const confirmPassword =
        field === 'confirmPassword'
          ? value
          : updatedValues.confirmPassword.value;

      if (confirmPassword) {
        updatedValues.confirmPassword = {
          ...updatedValues.confirmPassword,
          error: newPassword !== confirmPassword,
          errorMessage:
            newPassword !== confirmPassword ? 'Password does not match' : '',
        };
      }

      return updatedValues;
    });
  };

  const togglePasswordVisibility = (field) => {
    setFormValues((prevState) => ({
      ...prevState,
      [field]: {
        ...prevState[field],
        showPassword: !prevState[field].showPassword,
      },
    }));
  };

  const [showValidationComponent, setShowValidationComponent] = useState(false);

  const [passWordValidation, setPasswordValidation] = useState({
    minimumLength: false,
    number: false,
    specialCharacter: false,
    capitalCase: false,
    lowerCase: false,
  });
  useEffect(() => {
    const values = Object.values(passWordValidation);
    const allValid = values.every((value) => value);
    const someInvalid = values.some((value) => value);
    if (allValid) {
      const timer = setTimeout(() => {
        setShowValidationComponent(false);
      }, 2000);

      return () => clearTimeout(timer);
    } else if (someInvalid) {
      setShowValidationComponent(true);
    }
  }, [passWordValidation]);

  const [isLoading, setIsLoading] = useState(false);

  useEffect(() => {
    if (isLoading) {
      document.body.style.pointerEvents = 'none';
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    }

    return () => {
      document.body.style.pointerEvents = 'auto';
      document.body.style.overflow = 'auto';
    };
  }, [isLoading]);

  const handleSubmit = async (e) => {
    e.preventDefault();

    let isValid = true;

    // Validate new password
    if (!formValues.newPassword.value) {
      isValid = false;
      setFormValues((prevState) => ({
        ...prevState,
        newPassword: {
          ...prevState.newPassword,
          error: true,
          errorMessage: 'New password is required.',
        },
      }));
    }

    // Validate confirm password
    if (
      formValues.confirmPassword.value !== formValues.newPassword.value ||
      !formValues.confirmPassword.value
    ) {
      isValid = false;
      setFormValues((prevState) => ({
        ...prevState,
        confirmPassword: {
          ...prevState.confirmPassword,
          error: true,
          errorMessage:
            formValues.confirmPassword.value === ''
              ? 'Confirm password is required.'
              : 'Password does not match.',
        },
      }));
    }

    if (isValid) {
      try {
        setIsLoading(true); // 🔹 Start loader

        const response = await postApiCall(RESET_PASSWORD, {
          newPassword: formValues.newPassword.value,
          email: otpResponseObj.email,
          otp: otpResponseObj.otp,
          userId: otpResponseObj.userId,
        });

        if (response?.data?.status === 1) {
          setShowForgotPassword(false);
          setShowsendOTP(false);
          setShowNewPassword(false);

          Swal.fire({
            title: response?.data?.heading,
            text: response?.data?.message,
            showDenyButton: false,
            showCancelButton: false,
            icon: 'success',
            confirmButtonText: 'Close',
          });
        } else {
          Swal.fire({
            title: 'Error',
            text:
              response?.data?.message ||
              'Failed to reset password. Please try again.',
            icon: 'error',
            confirmButtonText: 'Close',
          });
        }
      } catch (error) {
        alert('Network error. Please try again later.');
      } finally {
        setIsLoading(false); // 🔹 Stop loader
      }
    }
  };

  return (
    <>
      <Box
        sx={{
          display: 'flex',
          justifyContent: 'left',
          alignItems: 'left',
          minHeight: '10vh',
        }}
      >
        <Box
          sx={{
            width: { xs: '90%', sm: '400px' },
            borderRadius: '8px',
          }}
        >
          {' '}
          <Typography component="label" sx={loginLabelEmail}>
            New Password
          </Typography>
          <Box component="form">
            {/* New Password Field */}
            <TextField
              fullWidth
              type={formValues.newPassword.showPassword ? 'text' : 'password'}
              value={formValues.newPassword.value}
              onChange={(e) => handleInputChange('newPassword', e.target.value)}
              error={formValues.newPassword.error}
              placeholder="••••••••"
              helperText={formValues.newPassword.errorMessage}
              sx={{
                marginBottom: '16px',
                '& .MuiInputBase-input': {
                  fontFamily: 'Poppins',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Poppins',
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: 'Poppins',
                  fontWeight: 600, // Set font weight to 600
                  color: '#D32F2F',
                  marginLeft: '-0.1px',
                  fontSize: matchesmd ? '10px' : '12px',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      onClick={() => togglePasswordVisibility('newPassword')}
                      tabIndex={-1}
                      aria-label="toggle password visibility"
                    >
                      {formValues.newPassword.showPassword ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />
            <Collapse in={showValidationComponent} timeout={400} unmountOnExit>
              <PasswordValidation passWordValidation={passWordValidation} />
            </Collapse>

            {/* Confirm Password Field */}
            <Typography component="label" sx={loginLabelEmail}>
              Confirm Password
            </Typography>
            <TextField
              fullWidth
              type={
                formValues.confirmPassword.showPassword ? 'text' : 'password'
              }
              value={formValues.confirmPassword.value}
              onChange={(e) =>
                handleInputChange('confirmPassword', e.target.value)
              }
              error={formValues.confirmPassword.error}
              placeholder="••••••••"
              helperText={formValues.confirmPassword.errorMessage}
              sx={{
                marginBottom: '24px',
                '& .MuiInputBase-input': {
                  fontFamily: 'Poppins',
                },
                '& .MuiInputLabel-root': {
                  fontFamily: 'Poppins',
                },
                '& .MuiFormHelperText-root': {
                  fontFamily: 'Poppins',
                  fontWeight: 600, // Set font weight to 600
                  color: '#D32F2F',
                  marginLeft: '-0.1px',
                  fontSize: matchesmd ? '10px' : '12px',
                },
              }}
              InputProps={{
                endAdornment: (
                  <InputAdornment position="end">
                    <IconButton
                      tabIndex={-1}
                      onClick={() =>
                        togglePasswordVisibility('confirmPassword')
                      }
                    >
                      {formValues.confirmPassword.showPassword ? (
                        <Visibility />
                      ) : (
                        <VisibilityOff />
                      )}
                    </IconButton>
                  </InputAdornment>
                ),
              }}
            />

            {/* Buttons */}
            <Grid
              container
              spacing={2}
              sx={{ display: 'flex', justifyContent: 'space-between' }}
            >
              {/* Cancel Button */}
              <Grid item xs={5}>
                <Button
                  variant="outlined"
                  sx={{
                    padding: { xs: '6px 23px', sm: '10px 39px' },
                    width: '100%',
                    height: { xs: '35px', sm: '41px' },
                    fontSize: { xs: '12px', sm: '15px' },
                    color: '#666666',
                    fontWeight: '400',
                    fontFamily: 'Poppins',
                    border: '1px solid rgba(17, 35, 71, 0.1)',
                    borderRadius: '5px',
                    textTransform: 'none',
                  }}
                  onClick={() => {
                    setShowForgotPassword(false);
                    setShowNewPassword(false);
                    setShowsendOTP(false);
                  }}
                >
                  Cancel
                </Button>
              </Grid>

              <Grid
                item
                xs={5}
                sx={{
                  display: 'flex',
                  alignItems: 'center',
                  gap: '8px', // 🔹 only adds small space between button and loader
                }}
              >
                <Button
                  variant="outlined"
                  type="submit"
                  sx={{
                    padding: { xs: '6px 23px', sm: '10px 39px' },
                    width: '100%',
                    height: { xs: '35px', sm: '41px' },
                    fontSize: { xs: '12px', sm: '15px' },
                    color: '#FFFFFF',
                    fontWeight: '400',
                    fontFamily: 'Poppins',
                    backgroundColor: '#4CA4D6',
                    borderRadius: '5px',
                    textTransform: 'none',
                    '&:hover': {
                      backgroundColor: '#3b92be', // Darker shade on hover
                    },
                  }}
                  onClick={handleSubmit}
                >
                  Submit
                </Button>

                {isLoading && (
                  <img
                    alt="Loader"
                    src={Loading}
                    className="loaderImage"
                    style={{ height: '40px', width: '40px' }}
                  />
                )}
              </Grid>
            </Grid>
          </Box>
        </Box>
      </Box>
    </>
  );
};

export default ShowNewPassword;

export const loginLabelEmail = {
  ...poppins300,
  color: 'black !important',
  fontWeight: '500 !important',
  paddingBottom: '5px',
};
